import { getConfig } from "../constants/config";
import { getTotalAmountOut } from "./getTotalAmountOut";
import { parseUnits, formatUnits } from "viem";

/**
 * Calculates the minimum output amount for a swap considering slippage tolerance.
 * Supports various input formats and slippage representations.
 *
 * @param {Object} params - Calculation parameters
 * @param {(string|bigint|Array)} params.variable - Amount or route to calculate minimum for
 *   - If string: Amount in raw or formatted units
 *   - If bigint: Amount in raw units
 *   - If Array: Route from getRoute
 * @param {(string|number)} [params.slippageAmount=config.slippage] - Slippage tolerance
 *   - If isBps=false: Percentage (e.g., "0.5" for 0.5%)
 *   - If isBps=true: Basis points (e.g., 10000 for 100%)
 * @param {Object} [params.options] - Calculation options
 * @param {boolean} [params.options.isRaw=true] - Controls input/output format:
 *   - true: Treats string inputs as raw units (BigInt), returns bigint
 *   - false: Treats string inputs as formatted (parseUnits), returns formatted string
 * @param {boolean} [params.options.isBps=false] - If true, slippage is in basis points
 * @param {number} [params.options.outTokenDecimals] - Decimals for output token
 *   Required when isRaw=false
 *
 * @returns {Promise<bigint|string>} The minimum output amount
 *   - bigint if isRaw=true (for contract calls)
 *   - formatted string if isRaw=false (for display/logging)
 *
 * @throws {Error} If variable or slippageAmount is missing/invalid
 * @throws {Error} If slippage calculation fails
 *
 * @example
 * // Example 1: Calculate min output for a route with 0.5% slippage
 * const minOut = calculateAmountOutMin({
 *   variable: route,
 *   slippageAmount: "0.5"
 * });
 *
 * @example
 * // Example 2: Calculate with basis points slippage
 * const minOut = calculateAmountOutMin({
 *   variable: "1000000000000000000", // 1 token in wei
 *   slippageAmount: 50, // 0.5% in bps
 *   options: {
 *     isRaw: true,
 *     isBps: true
 *   }
 * });
 *
 * @example
 * // Example 3: Calculate with formatted amount
 * const minOut = calculateAmountOutMin({
 *   variable: "1.0",
 *   slippageAmount: "0.5",
 *   options: {
 *     isRaw: false,
 *     outTokenDecimals: 18
 *   }
 * });
 *
 * @note
 * - Returns original amount if slippage is 0
 * - Returns 0 if slippage >= 100% (or >= 10000 bps)
 * - Warns if slippage is unusually high
 */
export const calculateAmountOutMin = async ({
  variable, // route or amount
  slippageAmount = getConfig().slippage,
  options = {},
}) => {
  // Merge with defaults to handle partial options
  const mergedOptions = {
    isRaw: true,
    isBps: false,
    outTokenDecimals: undefined,
    ...options,
  };

  // Validate outTokenDecimals is provided when isRaw=false
  if (!mergedOptions.isRaw && mergedOptions.outTokenDecimals === undefined) {
    throw new Error(
      "Error: outTokenDecimals is required when isRaw=false. Please provide the token decimals."
    );
  }

  let amountOut = 0n;
  let slippage = 0;
  if (
    !variable ||
    !slippageAmount ||
    (Array.isArray(variable) && variable?.length === 0)
  ) {
    throw new Error("Invalid or missing `variable` or `slippageAmount` arg.");
  }

  //If variable is Route
  if (Array.isArray(variable) && variable.length !== 0)
    amountOut = (await getTotalAmountOut(variable)).amountOut;

  //If variable is raw string
  if (typeof variable === "string" && mergedOptions.isRaw)
    amountOut = BigInt(variable);

  // If variable is formatted string
  if (typeof variable === "string" && !mergedOptions.isRaw)
    amountOut = parseUnits(variable, mergedOptions.outTokenDecimals);

  // If variable is bigint
  if (typeof variable === "bigint") amountOut = variable;

  // Validate slippage is not negative
  if (Number(slippageAmount) < 0) {
    throw new Error("Error setting slippage: Slippage amount cannot be negative.");
  }

  // if slippage is formatted string or float/number
  if (
    (typeof slippageAmount === "string" ||
      typeof slippageAmount === "number") &&
    !mergedOptions.isBps
  )
    slippage = (Number(slippageAmount) / 100).toString();
  // If slippage is bps units
  if (mergedOptions.isBps) slippage = (Number(slippageAmount) / 10000).toString();

  if (Number(slippageAmount) === 0) {
    console.error(
      "Slippage is set to 0. Please set slippage to a non-zero value if not intended."
    );
    if (mergedOptions.isRaw) return amountOut;
    return formatUnits(amountOut, mergedOptions.outTokenDecimals);
  }

  if (
    (Number(slippageAmount) >= 100 && !mergedOptions.isBps) ||
    (Number(slippageAmount) >= 10000 && mergedOptions.isBps)
  ) {
    console.error(
      "Slippage is set to 100% or higher. Please set slippage to a lower value if not intended."
    );
    return 0n;
  }

  if (!slippage || !amountOut) {
    if (!slippage) throw new Error("Error setting slippage!");
    if (!amountOut)
      throw new Error(
        "Error setting slippage: No amount out provided! This might mean swap value is too low. (Return token amount is 0)"
      );
  }
  const integerPart = slippage.split(".")[0] || "0";
  const fractionalPart = slippage.split(".")[1] || "";

  const numerator = (
    integerPart.toString() + fractionalPart.toString()
  ).replace(/^0+|0+$/g, "");

  const denominatorExp = fractionalPart.length;

  let amountOutMin =
    amountOut - (amountOut * BigInt(numerator)) / BigInt(10 ** denominatorExp);

  // NOTE: We do NOT deduct fees here!
  // The smart contract checks minTotalAmountOut BEFORE deducting fees.
  // Fees are deducted by the contract's _processFee function AFTER validation.
  // This ensures slippage protection is based on the swap result, not post-fee amount.
  //
  // Contract flow:
  // 1. Execute swaps -> finalTokenAmount
  // 2. Check: finalTokenAmount >= minTotalAmountOut (our calculated value)
  // 3. Process fees: amountToTransfer = finalTokenAmount - fees
  // 4. Transfer amountToTransfer to user
  //
  // Therefore: minTotalAmountOut = amountOut - slippage (NO fee deduction)

  if (amountOutMin < 0n) {
    console.error(
      "Error setting slippage: Minimum amount is negative. This might mean slippage value is higher than 100%. Minimum amount set to `0`."
    );
    return 0n;
  }

  // Return in requested format
  if (mergedOptions.isRaw) return amountOutMin;
  return formatUnits(amountOutMin, mergedOptions.outTokenDecimals);
};
